import { showErrorToast, showSuccessToast } from '../../../../utils/showToast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Modal } from '../../../../components/ui'
import { Button } from '../../../../components/ui/legacy'
import ConfirmationBox from '../../../../components/ui/ConfirmationBox'
import TableData, { Pagination } from '../../../../components/ui/TableData'
import { accountService } from '../../../../service/AccountService'
import { accountKeys } from '../../../../data/queryFactories'

const PopUpStatusTable = ({
  openPopUp,
  closePopUp,
  data = [],
  newStatusCount = {
    ACTIVE: 0,
    INACTIVE: 0,
  },
  columnMapping,
  columnWidths,
  reset,
}) => {
  const queryClient = useQueryClient()
  const [openUpdateConfirmation, setOpenUpdateConfirmation] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const DataPerPage = 8

  useEffect(() => {
    setCurrentPage(1)
  }, [data])

  const totalData = data ? data.length : 0
  const totalPage =
    totalData <= DataPerPage ? 1 : Math.ceil(totalData / DataPerPage)

  const bulkUpdateMutation = useMutation({
    mutationFn: async (accounts) => {
      const results = await Promise.all(
        accounts.map(async (account) => {
          const payload = {
            ...account,
            status: account.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
          }
          try {
            const result = await accountService.update({
              uuid: account.uuid,
              payload: payload,
            })
            return {
              success: result.success,
              message: result.message,
              id: account.id,
            }
          } catch (error) {
            return {
              success: false,
              message: error.message,
              id: account.id,
            }
          }
        })
      )

      const failedUpdates = results.filter((result) => !result.success)

      if (failedUpdates.length > 0) {
        throw new Error(
          `Có lỗi khi cập nhật tài khoản ${failedUpdates.map((f) => f.id).join(', ')}`
        )
      }

      return results
    },
    onError: (error) => {
      showErrorToast({
        message: error.message,
        error: 'Vui lòng liên hệ Admin',
      })
    },
    onSuccess: () => {
      showSuccessToast('Cập nhật trạng thái thành công')
      queryClient.invalidateQueries({ queryKey: accountKeys.all })
      reset()
    },
    onSettled: () => {
      closePopUp()
      setOpenUpdateConfirmation(false)
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    bulkUpdateMutation.mutate(data)
  }

  if (!openPopUp) return null

  return (
    <Modal
      onClose={closePopUp}
      className="h-[80vh] max-h-[100%] w-[65vw] max-w-[1544px] overflow-y-auto rounded-lg bg-white px-8"
    >
      <div className="my-4 space-y-6">
        <h1 className="mt-6 text-4xl font-extrabold">
          Thay đổi trạng thái các tài khoản
        </h1>

        <div className="mb-2 flex items-center justify-between">
          <p className="font-semibold text-gray-secondary">
            XEM TRƯỚC THAY ĐỔI
          </p>

          <p className="font-semibold text-red-accent">
            {newStatusCount.ACTIVE} tài khoản sẽ bị khóa,{' '}
            {newStatusCount.INACTIVE} tài khoản sẽ được mở khóa
          </p>
        </div>

        <p className="font-semibold text-gray-secondary">
          DANH SÁCH TÀI KHOẢN ĐƯỢC THAY ĐỔI TRẠNG THÁI ({totalData})
        </p>
      </div>

      <div className="overflow-y-hidden">
        <TableData
          data={data.slice(
            (currentPage - 1) * DataPerPage,
            currentPage * DataPerPage
          )}
          columnMapping={columnMapping}
          columnWidths={columnWidths}
          paginationEnabled={false}
          pageSize={5}
          currentPage={currentPage + 1}
          totalPages={totalPage}
          totalItems={totalData}
        />
      </div>
      <div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPage}
          onPageChange={(page) => setCurrentPage(page)}
          totalItems={totalData}
          itemName={'tài khoản'}
        />
      </div>
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant={'tertiary'} onClickHandler={closePopUp}>
          Hủy
        </Button>
        <Button
          variant={'primary'}
          onClickHandler={() => {
            setOpenUpdateConfirmation(true)
          }}
        >
          Cập nhật
        </Button>
      </div>

      {openUpdateConfirmation && (
        <ConfirmationBox
          title="Xác nhận thay đổi trạng thái"
          message=" "
          onConfirm={handleSubmit}
          onCancel={() => setOpenUpdateConfirmation(false)}
        />
      )}
    </Modal>
  )
}

export default PopUpStatusTable
